import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { TAGS } from '@web-app/components/PatientTags/constants';
import { SessionReminderModal } from '@web-app/components/SessionReminderModal';
import { Button } from '@web-app/components/ui/Button/button';
import { GROWTHBOOK_FEATURE_FLAGS } from '@web-app/constants/growthbook';
import { HealthieTag } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useIsPrescriber } from '@web-app/hooks/useIsPrescriber';
import { hasPatientTag } from '@web-app/utils/patient-tags.utils';
import React, { useRef } from 'react';

interface ZocdocReminderModalProps {
  tags?: HealthieTag[];
}

const ZocdocReminderModal: React.FC<ZocdocReminderModalProps> = ({ tags }) => {
  const isEnabled = useFeatureIsOn(
    GROWTHBOOK_FEATURE_FLAGS.ZOC_DOC_POPUP_ENABLED,
  );
  const { isPrescriber, isLoading, isError } = useIsPrescriber();
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const initialFocusRef = useRef<HTMLElement>(null);

  if (
    !isEnabled ||
    !isPrescriber ||
    isLoading ||
    isError ||
    !hasPatientTag(tags, TAGS.ZOCDOC_GLP_1)
  ) {
    return null;
  }

  return (
    <SessionReminderModal
      popupId="zocdoc-patient-reminder"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay />
      <ModalContent
        ref={initialFocusRef as React.Ref<HTMLElement>}
        tabIndex={-1}
        sx={{ outline: 'none' }}
        bg="bg.default"
        color="text.default"
      >
        <ModalHeader>Zocdoc Patient Reminder</ModalHeader>

        <ModalBody>
          This patient booked through <strong>Zocdoc</strong> for GLP-1 care.
          Zocdoc patients come in with less intake than other patients, so
          please complete the full intake discussion during this visit based on
          the template.
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={onClose}
            className="w-full bg-primary-legacy text-primary-foreground hover:bg-primary-legacy/90"
          >
            Got it!
          </Button>
        </ModalFooter>
      </ModalContent>
    </SessionReminderModal>
  );
};

export default ZocdocReminderModal;
